<template>
  <section class="page activityPage">
    <header class="pageHeader">
      <div class="pageHeader__Inner">
        <h1 class="pageHeader__Title activityTitle">
          Activity
          <span
            class="activityLiveDot"
            :class="{ 'is-Idle': !hasActiveEditors }"
            :aria-label="hasActiveEditors ? 'Users are currently editing' : 'No active editors right now'"
            :title="hasActiveEditors ? 'Active editors online' : 'No active editors'"
          ></span>
        </h1>
      </div>
    </header>

    <div class="tab">
      <div class="tab__Wrapper">
        <div class="tab__Item">
          <div class="tab__Content">
            <div class="activityGrid">
              <div class="activityGrid__Row activityGrid__Row--Header">
                <div class="activityGrid__Cell activityGrid__Cell--Person">Person editing</div>
                <div class="activityGrid__Cell">Resource title</div>
                <div class="activityGrid__Cell">Link</div>
              </div>

              <template v-if="items.length > 0">
                <div class="activityGrid__Row" v-for="item in items" :key="item.id">
                  <div class="activityGrid__Cell activityGrid__Cell--Person">
                    <div class="editorCell">
                      <img
                        class="editorAvatarImage"
                        :src="item.editorImage"
                        alt=""
                        v-if="hasUsableImage(item)"
                        @error="markImageBroken(item.id)"
                      />
                      <span class="editorAvatar" v-else>{{ initials(item.editorName) }}</span>
                      <span class="editorName">{{ item.editorName }}</span>
                    </div>
                  </div>
                  <div class="activityGrid__Cell resourceTitle">{{ item.resourceTitle }}</div>
                  <div class="activityGrid__Cell">
                    <div class="activityButtonWrapper">
                      <a class="button" :href="item.resourceUrl" target="_blank" rel="noopener noreferrer">
                        Open resource
                      </a>
                    </div>
                  </div>
                </div>
              </template>

              <div class="activityGrid__Empty" v-if="items.length === 0">
                <p>No resources are currently being edited.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import HTTP from '@/http-common.js'

const TYPE_ROUTE_MAP = {
  blog: '/blog',
  page: '/pages',
  campaign: '/campaigns',
  project: '/projects',
  article: '/news',
  doc: '/publications',
  pdf: '/publications',
  ppt: '/publications',
  xls: '/publications',
  digital_publication: '/digital-publications',
  clause: '/clauses',
  audio: '/multimedia',
  image: '/multimedia',
  video: '/multimedia',
  case_study: '/case-studies',
  event: '/events',
}

export default {
  name: 'Activity',
  data() {
    return {
      activityItems: [],
      presenceApi: 'https://translationspsi.com/getAllActiveUsers',
      brokenImages: {},
      isRefreshing: false,
      refreshTimer: null,
    }
  },
  computed: {
    items() {
      return this.activityItems
    },
    hasActiveEditors() {
      return this.items.length > 0
    },
    user() {
      return this.$store.state.accounts.user
    },
    apiKeys() {
      const websites = this.$store.state.accounts.org && this.$store.state.accounts.org.websites
      if (!Array.isArray(websites)) return []
      return websites.map((website) => website.api_key)
    },
  },
  methods: {
    buildFallbackResourceUrl(route) {
      if (!route) return '#'
      if (typeof window === 'undefined') return route
      if (route.startsWith('http://') || route.startsWith('https://')) return route
      const normalizedRoute = route.startsWith('/') ? route : `/${route}`
      return `${window.location.origin}/#${normalizedRoute}`
    },
    getResourceRoute(resource) {
      if (!resource || !resource.id) return '/'

      if (resource.resourceRoute) return resource.resourceRoute

      const routeRoot = TYPE_ROUTE_MAP[resource.type] || '/news'
      return `${routeRoot}/build/${resource.id}`
    },
    async fetchResourceDetails(resourceIds) {
      const uniqueIds = [...new Set(resourceIds.filter((id) => id !== undefined && id !== null && id !== ''))]
      const requests = uniqueIds.map((id) =>
        HTTP.post(`resource/${id}`, {
          api_keys: this.apiKeys,
        })
          .then((response) => ({ id: String(id), resource: response.data }))
          .catch(() => ({ id: String(id), resource: null }))
      )

      const results = await Promise.all(requests)
      return results.reduce((accumulator, result) => {
        accumulator[result.id] = result.resource
        return accumulator
      }, {})
    },
    async getAllActiveUsers() {
      if (this.isRefreshing) return

      try {
        this.isRefreshing = true
        const response = await fetch(this.presenceApi, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            authorId: this.user && this.user.id ? this.user.id : null,
          }),
        })

        const data = await response.json()

        if (!data.success || !Array.isArray(data.activeUsers)) {
          console.warn('Failed to fetch all active users:', data.error || 'Invalid response')
          this.activityItems = []
          return
        }

        const resourcesById = await this.fetchResourceDetails(data.activeUsers.map((user) => user.resourceId))

        this.activityItems = data.activeUsers.map((user, index) => {
          const resourceId = String(user.resourceId || '')
          const resource = resourcesById[resourceId]
          const resourceTitle =
            user.resourceTitle ||
            (resource &&
              resource.l10n &&
              resource.l10n[0] &&
              resource.l10n[0].title &&
              resource.l10n[0].title.trim()) ||
            `Resource #${resourceId || 'unknown'}`

          const route = user.resourceRoute || this.getResourceRoute(resource || { id: resourceId, type: user.type })
          const resourceUrl = user.resourceUrl || this.buildFallbackResourceUrl(route)

          return {
            id: user.id ? `activity-${user.id}-${resourceId || index}` : `activity-${index}`,
            editorName: user.name || 'Unknown user',
            resourceTitle,
            resourceUrl,
            editorImage: user.image || user.authorImage || null,
          }
        })
      } catch (error) {
        console.error('Error fetching all active users:', error)
        this.activityItems = []
      } finally {
        this.isRefreshing = false
      }
    },
    hasUsableImage(item) {
      if (!item || !item.id || !item.editorImage) return false
      if (this.brokenImages[item.id]) return false
      if (typeof item.editorImage !== 'string') return false
      return item.editorImage.trim().length > 0
    },
    markImageBroken(itemId) {
      this.$set(this.brokenImages, itemId, true)
    },
    initials(name) {
      if (!name) return '?'
      const words = name.trim().split(/\s+/)
      if (words.length === 1) return words[0].charAt(0).toUpperCase()
      return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase()
    },
  },
  mounted() {
    this.getAllActiveUsers()
    this.refreshTimer = setInterval(() => {
      this.getAllActiveUsers()
    }, 20 * 1000)
  },
  beforeDestroy() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
      this.refreshTimer = null
    }
  },
}
</script>
